import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

import { checkUserAccess } from "@/hooks/useAccessControl";
import { repairTrialState } from "@/services/repairTrialState";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const rememberManualDestination = () => {
    if (location.pathname === "/creer-boutique-manuelle") {
      sessionStorage.setItem("redirectAfterLogin", location.pathname + location.search);
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (error) throw error;

        if (!session) {
          rememberManualDestination();
          toast({
            title: "Accès refusé",
            description: "Veuillez vous connecter pour accéder à cette page",
            variant: "destructive",
          });
          navigate("/connexion");
          return;
        }

        let access = await checkUserAccess(session.user.id, session.user.email);
        if (access.level === "blocked") {
          // Fill missing manual-trial dates, then re-check BEFORE redirecting
          const repaired = await repairTrialState();
          if (repaired) access = await checkUserAccess(session.user.id, session.user.email);
        }

        // /abonnement must stay reachable after the trial ends (post-trial subscription).
        const choicePaths = ["/gestion-compte", "/creer-boutique-manuelle", "/abonnement"];
        if (access.level === "blocked" && !choicePaths.includes(location.pathname)) {
          toast({
            title: "Choisissez votre mode de création",
            description:
              "Démarrez l'essai gratuit de 14 jours (création manuelle) ou créez une boutique IA.",
          });

          {
            navigate("/gestion-compte", { replace: true });
            return;
          }
        }

        setIsAuthenticated(true);
      } catch (error) {
        rememberManualDestination();
        console.error("Auth error:", error);
        toast({
          title: "Erreur",
          description: "Une erreur est survenue lors de la vérification de votre accès",
          variant: "destructive",
        });
        navigate("/connexion");
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        rememberManualDestination();
        setIsAuthenticated(false);
        navigate("/connexion");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, toast, location.pathname]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : null;
};

